class DOMSerializer {
    constructor(options = {}) {
      this.options = {
        includeStyles: true,
        includeScripts: false, // Security consideration
        includeFrames: true,
        includeShadowDOM: true,
        maxDepth: 50,
        ...options
      };
      this.serializedFrames = new Map();
      this.shadowRoots = new Map();
    }
  
    /**
     * Serialize a complete document or element
     */
    serialize(rootElement = document) {
      try {
        const serialized = {
          type: 'document',
          doctype: this.serializeDoctype(rootElement),
          documentElement: this.serializeElement(rootElement.documentElement || rootElement),
          frames: [],
          timestamp: Date.now(),
          url: rootElement.URL || window.location?.href,
          metadata: {
            title: rootElement.title,
            charset: rootElement.characterSet,
            contentType: rootElement.contentType
          }
        };
  
        // Serialize frames and iframes if enabled
        if (this.options.includeFrames) {
          serialized.frames = this.serializeFrames(rootElement);
        }
  
        return serialized;
      } catch (error) {
        console.error('Serialization error:', error);
        throw new Error(`DOM serialization failed: ${error.message}`);
      }
    }
  
    /**
     * Serialize document type declaration
     */
    serializeDoctype(doc) {
      if (!doc.doctype) return null;
      
      return {
        name: doc.doctype.name,
        publicId: doc.doctype.publicId,
        systemId: doc.doctype.systemId
      };
    }
  
    /**
     * Serialize an individual element and its children
     */
    serializeElement(element, depth = 0) {
      if (depth > this.options.maxDepth) {
        return { type: 'text', content: '<!-- Max depth exceeded -->' };
      }
  
      const nodeType = element.nodeType;
      
      switch (nodeType) {
        case Node.ELEMENT_NODE:
          return this.serializeElementNode(element, depth);
        case Node.TEXT_NODE:
          return this.serializeTextNode(element);
        case Node.COMMENT_NODE:
          return this.serializeCommentNode(element);
        case Node.DOCUMENT_FRAGMENT_NODE:
          return this.serializeDocumentFragment(element, depth);
        default:
          return null;
      }
    }
  
    /**
     * Serialize element node with attributes and children
     */
    serializeElementNode(element, depth) {
      const tagName = element.tagName.toLowerCase();
      
      // Skip script tags for security unless explicitly enabled
      if (tagName === 'script' && !this.options.includeScripts) {
        return { type: 'comment', content: '<!-- Script tag removed for security -->' };
      }
  
      const serialized = {
        type: 'element',
        tagName: tagName,
        attributes: this.serializeAttributes(element),
        children: [],
        shadowRoot: null
      };
  
      // Handle Shadow DOM
      if (this.options.includeShadowDOM && element.shadowRoot) {
        serialized.shadowRoot = this.serializeShadowRoot(element.shadowRoot, depth + 1);
      }
  
      // Handle special elements
      if (tagName === 'iframe' || tagName === 'frame') {
        serialized.frameData = this.serializeFrameElement(element);
      }
  
      // Serialize children
      for (const child of element.childNodes) {
        const serializedChild = this.serializeElement(child, depth + 1);
        if (serializedChild) {
          serialized.children.push(serializedChild);
        }
      }
  
      // Include computed styles if enabled
      if (this.options.includeStyles && element.nodeType === Node.ELEMENT_NODE) {
        serialized.computedStyle = this.serializeComputedStyle(element);
      }
  
      return serialized;
    }
  
    /**
     * Serialize element attributes
     */
    serializeAttributes(element) {
      const attributes = {};
      
      if (element.attributes) {
        for (const attr of element.attributes) {
          attributes[attr.name] = attr.value;
        }
      }
  
      return attributes;
    }
  
    /**
     * Serialize computed styles
     */
    serializeComputedStyle(element) {
      try {
        const computedStyle = window.getComputedStyle(element);
        const styles = {};
        
        // Only serialize non-default values to reduce size
        const importantStyles = [
          'display', 'position', 'width', 'height', 'margin', 'padding',
          'border', 'background', 'color', 'font-family', 'font-size',
          'text-align', 'visibility', 'z-index', 'transform'
        ];
  
        for (const prop of importantStyles) {
          const value = computedStyle.getPropertyValue(prop);
          if (value && value !== 'initial' && value !== 'normal') {
            styles[prop] = value;
          }
        }
  
        return styles;
      } catch (error) {
        return {};
      }
    }
  
    /**
     * Serialize text node
     */
    serializeTextNode(node) {
      return {
        type: 'text',
        content: node.textContent
      };
    }
  
    /**
     * Serialize comment node
     */
    serializeCommentNode(node) {
      return {
        type: 'comment',
        content: node.textContent
      };
    }
  
    /**
     * Serialize document fragment
     */
    serializeDocumentFragment(fragment, depth) {
      const serialized = {
        type: 'fragment',
        children: []
      };
  
      for (const child of fragment.childNodes) {
        const serializedChild = this.serializeElement(child, depth + 1);
        if (serializedChild) {
          serialized.children.push(serializedChild);
        }
      }
  
      return serialized;
    }
  
    /**
     * Serialize Shadow DOM
     */
    serializeShadowRoot(shadowRoot, depth) {
      const serialized = {
        type: 'shadowRoot',
        mode: shadowRoot.mode,
        children: []
      };
  
      for (const child of shadowRoot.childNodes) {
        const serializedChild = this.serializeElement(child, depth + 1);
        if (serializedChild) {
          serialized.children.push(serializedChild);
        }
      }
  
      return serialized;
    }
  
    /**
     * Serialize frame/iframe elements
     */
    serializeFrameElement(frameElement) {
      const frameData = {
        src: frameElement.src,
        name: frameElement.name,
        id: frameElement.id,
        sandbox: frameElement.sandbox?.toString() || '',
        allowfullscreen: frameElement.allowFullscreen
      };
  
      // Try to access frame content (may fail due to CORS)
      try {
        const frameDoc = frameElement.contentDocument;
        if (frameDoc && this.options.includeFrames) {
          frameData.content = this.serialize(frameDoc);
        }
      } catch (error) {
        frameData.accessError = 'Cross-origin frame content not accessible';
      }
  
      return frameData;
    }
  
    /**
     * Serialize all frames in document
     */
    serializeFrames(doc) {
      const frames = [];
      const frameElements = doc.querySelectorAll('iframe, frame');
  
      for (const frameElement of frameElements) {
        try {
          const frameDoc = frameElement.contentDocument;
          if (frameDoc) {
            frames.push({
              element: this.serializeElement(frameElement),
              content: this.serialize(frameDoc)
            });
          }
        } catch (error) {
          frames.push({
            element: this.serializeElement(frameElement),
            error: 'Frame content not accessible'
          });
        }
      }
  
      return frames;
    }
  
    /**
     * Deserialize serialized DOM data back to DOM nodes
     */
    deserialize(serializedData, targetDocument = document) {
      try {
        if (serializedData.type === 'document') {
          return this.deserializeDocument(serializedData, targetDocument);
        } else {
          return this.deserializeElement(serializedData, targetDocument);
        }
      } catch (error) {
        console.error('Deserialization error:', error);
        throw new Error(`DOM deserialization failed: ${error.message}`);
      }
    }
  
    /**
     * Deserialize complete document
     */
    deserializeDocument(serializedDoc, targetDoc) {
      // Create new document if needed
      const doc = targetDoc || document.implementation.createHTMLDocument();
  
      // Set doctype if present
      if (serializedDoc.doctype) {
        const doctype = document.implementation.createDocumentType(
          serializedDoc.doctype.name,
          serializedDoc.doctype.publicId,
          serializedDoc.doctype.systemId
        );
        doc.replaceChild(doctype, doc.doctype);
      }
  
      // Deserialize document element
      if (serializedDoc.documentElement) {
        const newDocElement = this.deserializeElement(serializedDoc.documentElement, doc);
        doc.replaceChild(newDocElement, doc.documentElement);
      }
  
      // Handle metadata
      if (serializedDoc.metadata) {
        doc.title = serializedDoc.metadata.title || '';
      }
  
      return doc;
    }
  
    /**
     * Deserialize individual element
     */
    deserializeElement(serializedNode, doc) {
      switch (serializedNode.type) {
        case 'element':
          return this.deserializeElementNode(serializedNode, doc);
        case 'text':
          return doc.createTextNode(serializedNode.content);
        case 'comment':
          return doc.createComment(serializedNode.content);
        case 'fragment':
          return this.deserializeDocumentFragment(serializedNode, doc);
        case 'shadowRoot':
          // Shadow roots are handled during element creation
          return null;
        default:
          return null;
      }
    }
  
    /**
     * Deserialize element node
     */
    deserializeElementNode(serializedElement, doc) {
      const element = doc.createElement(serializedElement.tagName);
  
      // Set attributes
      if (serializedElement.attributes) {
        for (const [name, value] of Object.entries(serializedElement.attributes)) {
          try {
            element.setAttribute(name, value);
          } catch (error) {
            console.warn(`Failed to set attribute ${name}:`, error);
          }
        }
      }
  
      // Apply computed styles if available
      if (serializedElement.computedStyle && this.options.includeStyles) {
        for (const [prop, value] of Object.entries(serializedElement.computedStyle)) {
          try {
            element.style.setProperty(prop, value);
          } catch (error) {
            console.warn(`Failed to set style ${prop}:`, error);
          }
        }
      }
  
      // Create shadow root if present
      if (serializedElement.shadowRoot && element.attachShadow) {
        try {
          const shadowRoot = element.attachShadow({ 
            mode: serializedElement.shadowRoot.mode || 'open' 
          });
          
          // Deserialize shadow root children
          for (const child of serializedElement.shadowRoot.children) {
            const childElement = this.deserializeElement(child, doc);
            if (childElement) {
              shadowRoot.appendChild(childElement);
            }
          }
        } catch (error) {
          console.warn('Failed to create shadow root:', error);
        }
      }
  
      // Deserialize children
      if (serializedElement.children) {
        for (const child of serializedElement.children) {
          const childElement = this.deserializeElement(child, doc);
          if (childElement) {
            element.appendChild(childElement);
          }
        }
      }
  
      // Handle frame content
      if (serializedElement.frameData && serializedElement.frameData.content) {
        // Frame content deserialization would happen after the frame loads
        element.addEventListener('load', () => {
          try {
            const frameDoc = element.contentDocument;
            if (frameDoc) {
              this.deserializeDocument(serializedElement.frameData.content, frameDoc);
            }
          } catch (error) {
            console.warn('Failed to deserialize frame content:', error);
          }
        });
      }
  
      return element;
    }
  
    /**
     * Deserialize document fragment
     */
    deserializeDocumentFragment(serializedFragment, doc) {
      const fragment = doc.createDocumentFragment();
  
      if (serializedFragment.children) {
        for (const child of serializedFragment.children) {
          const childElement = this.deserializeElement(child, doc);
          if (childElement) {
            fragment.appendChild(childElement);
          }
        }
      }
  
      return fragment;
    }
  }
  
  // Usage example and utility functions
  class DOMUtils {
    /**
     * Create serializer with common presets
     */
    static createSerializer(preset = 'default') {
      const presets = {
        default: {
          includeStyles: true,
          includeScripts: false,
          includeFrames: true,
          includeShadowDOM: true
        },
        minimal: {
          includeStyles: false,
          includeScripts: false,
          includeFrames: false,
          includeShadowDOM: false
        },
        complete: {
          includeStyles: true,
          includeScripts: true,
          includeFrames: true,
          includeShadowDOM: true
        },
        secure: {
          includeStyles: true,
          includeScripts: false,
          includeFrames: false,
          includeShadowDOM: true
        }
      };
  
      return new DOMSerializer(presets[preset] || presets.default);
    }
  
    /**
     * Serialize DOM to JSON string
     */
    static serializeToJSON(element, options) {
      const serializer = new DOMSerializer(options);
      const serialized = serializer.serialize(element);
      return JSON.stringify(serialized, null, 2);
    }
  
    /**
     * Deserialize from JSON string
     */
    static deserializeFromJSON(jsonString, targetDocument) {
      const serialized = JSON.parse(jsonString);
      const serializer = new DOMSerializer();
      return serializer.deserialize(serialized, targetDocument);
    }
  
    /**
     * Clone DOM with full fidelity including Shadow DOM
     */
    static deepClone(element, options) {
      const serializer = new DOMSerializer(options);
      const serialized = serializer.serialize(element);
      return serializer.deserialize(serialized, element.ownerDocument);
    }
  
    /**
     * Compare two DOM structures
     */
    static compare(element1, element2, options) {
      const serializer = new DOMSerializer(options);
      const serialized1 = serializer.serialize(element1);
      const serialized2 = serializer.serialize(element2);
      
      return JSON.stringify(serialized1) === JSON.stringify(serialized2);
    }
  }
  
  /*
  // Export for use
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { DOMSerializer, DOMUtils };
  } else if (typeof window !== 'undefined') {
    window.DOMSerializer = DOMSerializer;
    window.DOMUtils = DOMUtils;
  }
  */

  /* Usage Examples:
  
  // Basic serialization
  const serializer = new DOMSerializer();
  const serialized = serializer.serialize(document);
  console.log(JSON.stringify(serialized, null, 2));
  
  // Deserialize back to DOM
  const clonedDoc = serializer.deserialize(serialized);
  
  // Using presets
  const minimalSerializer = DOMUtils.createSerializer('minimal');
  const secureSerializer = DOMUtils.createSerializer('secure');
  
  // Serialize specific element with Shadow DOM
  const customElement = document.querySelector('my-custom-element');
  const serializedElement = serializer.serialize(customElement);
  
  // JSON utilities
  const jsonString = DOMUtils.serializeToJSON(document.body);
  const restored = DOMUtils.deserializeFromJSON(jsonString);
  
  // Deep clone with Shadow DOM support
  const clone = DOMUtils.deepClone(document.body, { includeShadowDOM: true });
  
  */

  export function serializeNodeToJSON(nodeElement) {
    return DOMUtils.serializeToJSON(nodeElement, {includeStyles: false});
  }

  export function deserializeNodeFromJSON(jsonString) {
    return DOMUtils.deserializeFromJSON(jsonString);
  }
